/**
 * 상단 바.
 *
 * 프로토타입의 알림 종은 숫자가 박혀 있었다. 여기서는 **실제로 지금 문제인 것**을
 * 센다: 오프라인 화면, 마지막 재생에서 오류를 보고한 화면, 활성 긴급 오버라이드.
 * 셋 다 운영자가 즉시 손을 대야 하는 상태다. 세지 못할 때는 종을 조용히 둔다 —
 * 0 을 보여주면 "문제 없음"으로 읽히는데 그건 거짓말이다.
 *
 * 좁은 화면에서는 왼쪽에 메뉴 단추가 붙는다(사이드바가 서랍이 된다). 부제는 빼고
 * 제목만 남긴다 — 휴대폰 폭에서 두 줄을 넣으면 제목까지 잘린다.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Bell, ChevronDown, LogOut, Menu, MonitorX, Siren, UserCog } from 'lucide-react';
import type { SignageOneClient } from '../../api/client.js';
import { GlobalSearch } from './GlobalSearch.js';

export interface Alert {
  key: string;
  label: string;
  count: number;
  to: string;
  tone: 'warn' | 'danger';
  icon: typeof Bell;
}

export function Header({
  title,
  subtitle,
  client,
  alerts,
  principal,
  scope,
  transportKind,
  canSignOut,
  onSignOut,
  onOpenNav,
}: {
  title: string;
  subtitle: string;
  client: SignageOneClient;
  /** null 이면 아직 세지 못한 상태 — 0 과 구분한다. */
  alerts: Alert[] | null;
  principal: string;
  /** 담당 범위(맡은 그룹 이름). 범위가 없으면 null — 그 자리에 연결 방식이 보인다. */
  scope: string | null;
  transportKind: string;
  /** 로컬 계정으로 붙었을 때만 로그아웃이 의미가 있다. */
  canSignOut: boolean;
  onSignOut: () => void;
  /** 좁은 화면에서 메뉴 서랍을 연다. */
  onOpenNav: () => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const total = alerts?.reduce((sum, alert) => sum + alert.count, 0) ?? 0;

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-2 border-b border-slate-200/80 bg-white/80 px-4 backdrop-blur-md sm:gap-4 sm:px-6">
      <div className="flex min-w-0 flex-1 items-center gap-1.5 md:flex-initial">
        <button
          type="button"
          onClick={onOpenNav}
          aria-label="메뉴 열기"
          className="-ml-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 lg:hidden"
        >
          <Menu className="h-5 w-5" strokeWidth={1.9} />
        </button>
        <div className="min-w-0">
          <h2 className="truncate text-[15px] font-semibold text-slate-900">{title}</h2>
          {subtitle ? <p className="hidden truncate text-[12.5px] text-slate-400 sm:block">{subtitle}</p> : null}
        </div>
      </div>

      <GlobalSearch client={client} />

      <div className="flex shrink-0 items-center gap-1 sm:gap-3">
        <div className="relative">
          <button
            type="button"
            aria-label={total > 0 ? `주의가 필요한 항목 ${total}건` : '주의가 필요한 항목 없음'}
            onClick={() => setOpen((prev) => !prev)}
            className="relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-700"
          >
            <Bell className="h-[18px] w-[18px]" strokeWidth={1.8} />
            {total > 0 && (
              <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[11.5px] font-bold text-white shadow-sm">
                {total > 99 ? '99+' : total}
              </span>
            )}
          </button>

          {open && (
            <>
              <div className="fixed inset-0 z-40" role="presentation" onClick={() => setOpen(false)} />
              <div className="absolute right-0 top-11 z-50 w-[min(18rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                <p className="border-b border-slate-100 px-4 py-2.5 text-[12.5px] font-semibold text-slate-500">
                  주의가 필요한 항목
                </p>
                {alerts === null ? (
                  <p className="px-4 py-3 text-[13.5px] text-slate-400">상태를 불러오지 못했습니다</p>
                ) : total === 0 ? (
                  <p className="px-4 py-3 text-[13.5px] text-slate-400">모두 정상입니다</p>
                ) : (
                  <ul>
                    {alerts
                      .filter((alert) => alert.count > 0)
                      .map((alert) => {
                        const Icon = alert.icon;
                        return (
                          <li key={alert.key}>
                            <button
                              type="button"
                              onClick={() => {
                                navigate(alert.to);
                                setOpen(false);
                              }}
                              className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                            >
                              <Icon
                                className={`h-4 w-4 shrink-0 ${
                                  alert.tone === 'danger' ? 'text-red-500' : 'text-amber-500'
                                }`}
                                strokeWidth={2}
                              />
                              <span className="flex-1 text-[13.5px] text-slate-700">{alert.label}</span>
                              <span className="font-mono text-[13.5px] font-semibold text-slate-900">
                                {alert.count}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        <div className="hidden h-6 w-px bg-slate-200 sm:block" />

        {/*
          로그인한 주체를 그대로 보여준다. 호스트가 넣어준 신원으로 붙어 있을 수
          있어서, 어떤 경로로 붙었는지(transport)도 같이 드러낸다 — 통합 문제를
          디버깅할 때 첫 단서다.
        */}
        <div className="relative">
          <button
            type="button"
            aria-label="계정 메뉴"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex items-center gap-2.5 rounded-lg px-1 py-1.5 transition-colors hover:bg-slate-100 sm:px-2"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-th-5 to-th-4 shadow-sm">
              <span className="text-[11.5px] font-bold text-[#1a2e05]">{initials(principal)}</span>
            </div>
            <div className="hidden text-left md:block">
              <p className="max-w-[140px] truncate text-[13.5px] font-semibold text-slate-700">{principal}</p>
              {scope ? (
                <p className="max-w-[140px] truncate text-[11.5px] text-slate-500" title={`담당 범위: ${scope}`}>
                  담당 · {scope}
                </p>
              ) : (
                <p className="font-mono text-[11.5px] uppercase tracking-wide text-slate-400">{transportKind}</p>
              )}
            </div>
            <ChevronDown className="hidden h-3.5 w-3.5 text-slate-400 md:block" strokeWidth={2} />
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" role="presentation" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-12 z-50 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                {/* 좁은 화면에서는 이름이 단추에 안 보이므로 여기서 밝힌다. */}
                <div className="border-b border-slate-100 px-4 py-2.5 md:hidden">
                  <p className="truncate text-[13.5px] font-semibold text-slate-700">{principal}</p>
                  {scope ? (
                    <p className="truncate text-[11.5px] text-slate-500">담당 · {scope}</p>
                  ) : (
                    <p className="font-mono text-[11.5px] uppercase tracking-wide text-slate-400">{transportKind}</p>
                  )}
                </div>
                <Link
                  to="/account"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-[13.5px] text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <UserCog className="h-4 w-4 text-slate-400" strokeWidth={1.9} />
                  내 계정
                </Link>
                {canSignOut ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onSignOut();
                    }}
                    className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-left text-[13.5px] text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    <LogOut className="h-4 w-4 text-slate-400" strokeWidth={1.9} />
                    로그아웃
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export const ALERT_ICONS = { offline: MonitorX, erroring: AlertTriangle, emergency: Siren };

/**
 * 이름에서 표시용 두 글자.
 *
 * 한글은 한 글자만 쓴다 — 두 글자를 넣으면 동그라미를 넘친다.
 * `user:` `service:` 같은 종류 접두사는 떼어 낸다. 붙여 두면 모든 서비스 계정이
 * 똑같이 "SE" 로 보여서 구분하는 의미가 없어진다.
 */
export function initials(name: string): string {
  const trimmed = name.trim().replace(/^(user|service|device):/, '');
  if (!trimmed) return '??';
  if (/[가-힣]/.test(trimmed)) return trimmed.slice(0, 1);
  const [first, second] = trimmed.split(/[\s._@:-]+/).filter(Boolean);
  if (first && second) return (first.slice(0, 1) + second.slice(0, 1)).toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
}
